import {
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import { GLOBAL_DROP_DOWN } from "../../services/Endpoints";
import {
  DELETE_GALLERY_DIRECTORY,
  GET_ALL_GALLERY_DIRECTORY,
} from "../../services/gallery-directory/GalleryDirectoryService";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { LOOKUP_CATEGORY } from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
  toastSuccess,
} from "../../utils/AppFunctions";

export interface IGalleryDirectory {
  id: string;
  folder_name: string;
  folder_image: string | null;
  gender: string;
  is_active: TBitValue;
  created_by?: string;
  created_date?: string;
  modified_by?: string;
  modified_date?: string;
  gallery_item_count: string;
}

export function getGalleryDirectories<T>(
  customFolderImage: (item: T) => JSX.Element,
  actionComponent: (item: T) => JSX.Element,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("folder_name"),
      key: "folder_name",
      sort: true,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
      component: customFolderImage,
    },
    {
      name: t("gallery_items"),
      key: "gallery_item_count",
      sort: true,
      type: TABLE_FIELD_TYPE.status,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
      assistiveText: t("gallery_item_count"),
    },
    {
      name: t("gender"),
      key: "gender",
      sort: true,
      type: TABLE_FIELD_TYPE.status,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-1",
    },

    {
      name: t("created_by"),
      key: "created_by",
      sort: false,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("created_date"),
      key: "created_date",
      sort: true,
      type: TABLE_FIELD_TYPE.date,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("modified_by"),
      key: "modified_by",
      sort: false,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("modified_date"),
      key: "modified_date",
      sort: true,
      type: TABLE_FIELD_TYPE.date,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,

      flex: "col-span-2",
      component: actionComponent,
    },
  ];
}

export const fetchAllGalleryDirectory = async (
  setGalleryDirectory: TReactSetState<IGalleryDirectory[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: string
) => {
  try {
    const galleryDirectory = await GET_ALL_GALLERY_DIRECTORY(
      pagination,
      search,
      advanceSearch
    );
    if (
      galleryDirectory &&
      galleryDirectory.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setGalleryDirectory(galleryDirectory.data.items);
      setPagination(galleryDirectory.data.pagination);
    } else {
      setGalleryDirectory([]);
      sweetAlertError({
        message: galleryDirectory.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "fetchAllGalleryDirectory");
  }
};

export const deleteGalleryDirectory = async (id: string) => {
  try {
    const deletedData = await DELETE_GALLERY_DIRECTORY(id);
    if (deletedData && deletedData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: deletedData.message });
      return true;
    } else {
      sweetAlertError({
        message: deletedData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "deleteGalleryDirectory");
    return false;
  }
};

export const getGenderDdl = async (
  setGender: TReactSetState<IOption<string>[]>,
  selectedGanderValue?: string
) => {
  try {
    const payload = {
      entity: "sts_lookups",
      data_value: "data_value",
      display_value: "display_value",
      filters: [
        {
          field: "lookup_category",
          value: LOOKUP_CATEGORY.Gender,
          condition: "eq",
        },
      ],
      ...(selectedGanderValue && {
        lookup_values: [selectedGanderValue],
        lookup_category: LOOKUP_CATEGORY.Gender,
      }),
    };
    const genderDDLList = await GLOBAL_DROP_DOWN(payload);
    if (genderDDLList && genderDDLList.code === DEFAULT_STATUS_CODE_SUCCESS) {
      if (genderDDLList.data) {
        setGender(genderDDLList.data);
      } else {
        setGender([]);
      }
    } else {
      toastError(genderDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getGenderDdl");
  }
};
